import { useCallback, useEffect, useState } from 'react'
import { addPasskey, credentials, removePasskey, type Credential, type User } from '../lib/auth'
import { enablePush, pushState, type PushState } from '../lib/push'
import { get, post } from '../lib/api'
import { t } from '../lib/i18n'

interface Inbox {
  id: string
  title: string
  body: string | null
  target: string | null
  created_at: number
  read_at: number | null
}

/**
 * The account screen: passkeys, notifications, inbox.
 *
 * Device management is not an advanced setting here — with no password, the list
 * of registered passkeys IS the account's security, and the recovery warning is
 * the most important thing on the page.
 */
export default function Account({ user }: { user: User }) {
  const [creds, setCreds] = useState<Credential[]>([])
  const [warning, setWarning] = useState<string | null>(null)
  const [push, setPush] = useState<PushState>('off')
  const [inbox, setInbox] = useState<Inbox[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState('')

  const load = useCallback(async () => {
    const [c, p, n] = await Promise.all([
      credentials(),
      pushState(),
      get<{ notifications: Inbox[] }>('/api/notifications').catch(() => ({ notifications: [] })),
    ])
    setCreds(c.credentials)
    setWarning(c.warning)
    setPush(p)
    setInbox(n.notifications)
  }, [])

  useEffect(() => { void load() }, [load])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await load()
    } catch (err) {
      const name = (err as { name?: string }).name
      setError(name === 'NotAllowedError' || name === 'AbortError' ? t('auth.cancelled') : (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <section className="card">
        <h2>{t('account.passkeys')}</h2>

        {warning && <p className="warn" role="alert">{warning}</p>}

        <ul className="list">
          {creds.map((c) => (
            <li key={c.id}>
              <div>
                <strong>{c.deviceName}</strong>
                <div className="muted small">
                  {c.syncedToProvider ? t('account.synced') : t('account.deviceOnly')}
                  {' · '}
                  {/* A passkey enrolled but never used is one that never worked. */}
                  {c.lastUsedAt ? `${t('account.lastUsed')} ${new Date(c.lastUsedAt).toLocaleDateString()}` : t('account.neverUsed')}
                </div>
              </div>
              <button
                disabled={busy || creds.length <= 1}
                title={creds.length <= 1 ? 'This is the only way into the account.' : undefined}
                onClick={() => run(() => removePasskey(c.id))}
              >
                {t('account.removePasskey')}
              </button>
            </li>
          ))}
        </ul>

        <button className="primary" disabled={busy} onClick={() => run(() => addPasskey())}>
          {t('account.addPasskey')}
        </button>
        {error && <p className="error" role="alert">{error}</p>}
      </section>

      <section className="card">
        <h2>{t('notify.inbox')}</h2>

        {push === 'blocked' && <p className="warn">{t('notify.blocked')}</p>}
        {push === 'off' && (
          // Must be a real click: Safari refuses the permission prompt outside a
          // user gesture, and refuses it permanently for the origin.
          <button disabled={busy} onClick={() => run(() => enablePush(user.displayName))}>
            {t('notify.enable')}
          </button>
        )}
        {push === 'on' && <p className="muted small">{t('notify.enabled')}</p>}

        <ul className="list">
          {inbox.length === 0 && <li className="muted">{t('notify.empty')}</li>}
          {inbox.map((n) => (
            <li key={n.id} className={n.read_at ? 'read' : ''}>
              <div>
                <strong>{n.title}</strong>
                <div className="muted small">{n.body}</div>
              </div>
              {!n.read_at && (
                <button onClick={() => run(() => post(`/api/notifications/${n.id}/read`))}>Mark read</button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/*
        * Deleting the account.
        *
        * Apple's guideline 5.1.1(v) requires this to be STARTED here, in the
        * app — a support address is explicitly not enough — and Play requires
        * the same. So it is a real control, not a mailto.
        *
        * Two steps, and the second is typing the username rather than pressing
        * OK. There is no undo behind this button and a confirm dialog is
        * something a thumb dismisses without reading.
        */}
      <section className="card danger">
        <h2>{t('account.deleteTitle')}</h2>
        <p className="muted small">{t('account.deleteExplain')}</p>

        {!confirming ? (
          <button disabled={busy} onClick={() => { setConfirming(true); setTyped('') }}>
            {t('account.deleteStart')}
          </button>
        ) : (
          <>
            <label>
              {t('account.deleteConfirmLabel')}
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={user.handle}
              />
            </label>
            <button
              className="destructive"
              disabled={busy || typed.trim().toLowerCase() !== user.handle.toLowerCase()}
              onClick={() => run(async () => {
                await post('/api/account/delete', { confirm: typed.trim() })
                /*
                 * A full reload, not a state change. The session is gone on the
                 * server, so every component still mounted is holding data the
                 * account no longer owns — and React unwinding that produces a
                 * cascade of 401s rather than a clean sign-out.
                 */
                window.location.href = '/'
              })}
            >
              {t('account.deleteConfirm')}
            </button>
            <button disabled={busy} onClick={() => setConfirming(false)}>
              {t('account.deleteCancel')}
            </button>
          </>
        )}
      </section>
    </>
  )
}
